import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  ScrollArea,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  UnstyledButton
} from '@mantine/core';
import { FiRefreshCw, FiSearch } from 'react-icons/fi';
import {
  TbCheck,
  TbChevronRight,
  TbCloud,
  TbCloudUpload,
  TbFolder,
  TbPlugConnected
} from 'react-icons/tb';
import { getConnectionLabel, getProviderMeta } from '../../../../consts/integrationsCatalog';
const surfaceBorderColor = 'light-dark(#eceef2, var(--mantine-color-dark-5))';
const mutedTextColor = 'light-dark(#6b7280, var(--mantine-color-dark-2))';

const formatFileSize = value => {
  if (!value) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
};

const getFileMeta = (file, isFolderNavigationEnabled) => {
  if (file?.is_folder) {
    return isFolderNavigationEnabled ? 'Folder • Click to open' : 'Folder';
  }

  const sizeLabel = formatFileSize(file?.size);
  return [file?.mime_type || 'Unknown type', sizeLabel].filter(Boolean).join(' • ');
};

const ProviderLogo = ({ alt, logoPath, size = 18 }) => {
  return (
    <img src={logoPath} alt={alt} style={{ width: size, height: size, objectFit: 'contain' }} />
  );
};

const ProviderButton = ({ isActive, onClick, provider }) => {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        alignItems: 'center',
        backgroundColor: isActive
          ? 'light-dark(#eef4ff, var(--mantine-color-blue-light))'
          : 'transparent',
        borderRadius: 10,
        color: isActive
          ? 'light-dark(#111827, var(--mantine-color-dark-0))'
          : 'light-dark(#374151, var(--mantine-color-dark-1))',
        display: 'flex',
        fontSize: 13,
        fontWeight: isActive ? 600 : 500,
        gap: 10,
        padding: '10px 12px',
        transition: 'background-color 0.15s ease'
      }}
    >
      <Box
        style={{
          alignItems: 'center',
          backgroundColor: 'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
          borderRadius: 8,
          display: 'flex',
          height: 28,
          justifyContent: 'center',
          width: 28
        }}
      >
        <ProviderLogo alt={provider.label} logoPath={provider.logoPath} size={16} />
      </Box>
      <Text size="sm" fw={isActive ? 600 : 500} truncate>
        {provider.label}
      </Text>
    </UnstyledButton>
  );
};

const EmptyStateCard = ({ actionLabel, description, icon, onAction, title }) => {
  return (
    <Card
      withBorder
      radius="md"
      p="xl"
      style={{
        backgroundColor: 'light-dark(#fafafa, var(--mantine-color-dark-6))',
        borderColor: surfaceBorderColor
      }}
    >
      <Stack align="center" gap="sm">
        <Box
          style={{
            alignItems: 'center',
            backgroundColor: 'light-dark(#f0f1f4, var(--mantine-color-dark-5))',
            borderRadius: 14,
            color: mutedTextColor,
            display: 'flex',
            height: 52,
            justifyContent: 'center',
            width: 52
          }}
        >
          {icon}
        </Box>
        <Text fw={600} size="sm">
          {title}
        </Text>
        <Text size="sm" c="dimmed" ta="center" maw={280}>
          {description}
        </Text>
        {actionLabel && onAction ? <Button onClick={onAction}>{actionLabel}</Button> : null}
      </Stack>
    </Card>
  );
};

const CloudImportModal = ({
  acceptedMimeTypes,
  cloudPath,
  connections,
  files,
  hasAnyConnections,
  isCloudFolderNavigationEnabled,
  isFetchingCloudFiles,
  isFetchingConnections,
  isImporting,
  onClose,
  onConnectProvider,
  onFileClick,
  onImport,
  onNavigateBack,
  onRefreshConnections,
  onSearchChange,
  onSearchSubmit,
  onSelectConnection,
  onSelectProvider,
  onShowMoreIntegrations,
  opened,
  previewProviders,
  providers,
  searchValue,
  selectedCloudFiles,
  selectedConnectionId,
  selectedProvider,
  selectedProviderConnections,
  title
}) => {
  const [activeTab, setActiveTab] = useState('browse');
  const [integrationQuery, setIntegrationQuery] = useState('');

  useEffect(() => {
    if (!opened) {
      setActiveTab('browse');
      setIntegrationQuery('');
    }
  }, [opened]);

  const selectedProviderConfig = useMemo(
    () => providers.find(provider => provider.key === selectedProvider),
    [providers, selectedProvider]
  );

  const filteredProviders = useMemo(() => {
    const normalizedQuery = integrationQuery.trim().toLowerCase();

    if (!normalizedQuery) return previewProviders;

    return previewProviders.filter(provider =>
      provider.label.toLowerCase().includes(normalizedQuery)
    );
  }, [integrationQuery, previewProviders]);

  const selectedFilesCount = selectedCloudFiles.filter(file => !file.is_folder).length;
  const shouldShowNoConnectedState = !hasAnyConnections;
  const shouldShowSelectSourceState = hasAnyConnections && !selectedProvider;
  const selectedConnection = useMemo(
    () =>
      selectedProviderConnections.find(
        connection => connection.connection_id === selectedConnectionId
      ),
    [selectedConnectionId, selectedProviderConnections]
  );
  const sharepointPermissionCount =
    selectedConnection?.metadata?.sharepoint_sync_config?.permitted_folders?.length || 0;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="xl"
      centered
      radius={16}
      padding={0}
      styles={{
        content: { overflow: 'hidden' },
        header: {
          borderBottom: `1px solid ${surfaceBorderColor}`,
          marginBottom: 0,
          padding: '18px 22px 16px'
        },
        title: { fontSize: 14, fontWeight: 600 },
        body: { padding: 0 }
      }}
    >
      <Tabs value={activeTab} onChange={value => setActiveTab(value || 'browse')}>
        <Tabs.List px="lg" pt="sm" style={{ borderBottom: `1px solid ${surfaceBorderColor}` }}>
          <Tabs.Tab value="browse">Browse files</Tabs.Tab>
          <Tabs.Tab value="integrations">Integrations</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="browse">
          <Group align="stretch" gap={0} wrap="nowrap" style={{ minHeight: 520 }}>
            <Box
              w={220}
              style={{
                borderRight: `1px solid ${surfaceBorderColor}`,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Text px="md" pt="md" pb="xs" size="xs" fw={600} tt="uppercase" c="dimmed">
                Sources
              </Text>

              <ScrollArea.Autosize mah={390} mx="xs" mb="xs" type="auto">
                <Stack gap={4}>
                  {providers.map(provider => (
                    <ProviderButton
                      key={provider.key}
                      provider={provider}
                      isActive={provider.key === selectedProvider}
                      onClick={() => onSelectProvider(provider.key)}
                    />
                  ))}
                </Stack>
              </ScrollArea.Autosize>

              <Box p="sm" mt="auto">
                <Button variant="default" fullWidth onClick={() => setActiveTab('integrations')}>
                  Add source
                </Button>
              </Box>
            </Box>

            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
              <Group
                px="md"
                py="sm"
                wrap="nowrap"
                gap="sm"
                style={{
                  borderBottom: '1px solid light-dark(#f0f1f4, var(--mantine-color-dark-5))'
                }}
              >
                <TextInput
                  value={searchValue}
                  onChange={event => onSearchChange(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && onSearchSubmit) {
                      onSearchSubmit();
                    }
                  }}
                  placeholder="Search files..."
                  leftSection={
                    <FiSearch size={14} color="light-dark(#9ca3af, var(--mantine-color-dark-2))" />
                  }
                  styles={{
                    input: {
                      backgroundColor: 'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
                      borderColor: 'transparent'
                    }
                  }}
                  style={{ flex: 1 }}
                />

                <ActionIcon
                  variant="default"
                  size="lg"
                  loading={isFetchingConnections}
                  onClick={onRefreshConnections}
                  aria-label="Refresh sources"
                >
                  <FiRefreshCw size={14} />
                </ActionIcon>
              </Group>

              <Stack gap="md" p="md" style={{ flex: 1, minHeight: 0 }}>
                <div>
                  <Group justify="space-between" align="center" mb={8}>
                    <Group gap="xs">
                      {selectedProviderConfig ? (
                        <ProviderLogo
                          alt={selectedProviderConfig.label}
                          logoPath={selectedProviderConfig.logoPath}
                          size={16}
                        />
                      ) : null}
                      <Text fw={600} size="sm">
                        {selectedProviderConfig?.label || 'Source'}
                      </Text>
                    </Group>

                    {selectedProvider && selectedProviderConfig?.canConnect ? (
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => onConnectProvider(selectedProvider)}
                      >
                        Add source
                      </Button>
                    ) : null}
                  </Group>

                  <Text size="xs" c="dimmed" mb="sm">
                    Select a connected source before browsing available files.
                  </Text>

                  {selectedProviderConnections.length > 0 ? (
                    <Group gap="xs">
                      {selectedProviderConnections.map(connection => (
                        <Badge
                          key={connection.connection_id}
                          variant={
                            selectedConnectionId === connection.connection_id ? 'filled' : 'light'
                          }
                          size="lg"
                          style={{ cursor: 'pointer', textTransform: 'none' }}
                          onClick={() => onSelectConnection(connection.connection_id)}
                        >
                          {getConnectionLabel(connection)}
                        </Badge>
                      ))}
                    </Group>
                  ) : selectedProvider && hasAnyConnections ? (
                    <Text size="sm" c="dimmed">
                      No connected accounts found for {selectedProviderConfig?.label}.
                    </Text>
                  ) : null}
                </div>

                {selectedConnectionId ? (
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <Button
                      variant="subtle"
                      size="xs"
                      onClick={onNavigateBack}
                      disabled={!cloudPath}
                    >
                      Back
                    </Button>
                    <Text size="xs" c="dimmed" truncate>
                      Path: {cloudPath || '/'}
                    </Text>
                  </Group>
                ) : null}

                <ScrollArea h={250} type="auto">
                  <Stack gap="xs">
                    {shouldShowNoConnectedState ? (
                      <EmptyStateCard
                        icon={<TbPlugConnected size={24} />}
                        title="No connected sources yet"
                        description="Connect a source from the Integrations tab to start importing files."
                        actionLabel="Browse integrations"
                        onAction={() => setActiveTab('integrations')}
                      />
                    ) : shouldShowSelectSourceState ? (
                      <EmptyStateCard
                        icon={<TbFolder size={24} />}
                        title="Select a source"
                        description="Pick a connected source from the left to browse and import files."
                      />
                    ) : !selectedConnectionId ? (
                      <EmptyStateCard
                        icon={<TbFolder size={24} />}
                        title="Select a connected account"
                        description="Choose one of the connected accounts above to browse and import files."
                      />
                    ) : isFetchingCloudFiles ? (
                      <Group justify="center" py="xl">
                        <Loader size="sm" />
                      </Group>
                    ) : selectedProvider === 'sharepoint-online' &&
                      selectedConnectionId &&
                      !cloudPath &&
                      sharepointPermissionCount === 0 ? (
                      <EmptyStateCard
                        icon={<TbFolder size={24} />}
                        title="No permissions configured"
                        description="Go to Integrations and add SharePoint permissions for this source before importing files."
                        actionLabel="Open Integrations"
                        onAction={onShowMoreIntegrations}
                      />
                    ) : files.length === 0 ? (
                      <EmptyStateCard
                        icon={<TbCloud size={24} />}
                        title="No files found"
                        description="Try another search term or refresh the selected source."
                      />
                    ) : (
                      files.map(file => {
                        const isSelected = selectedCloudFiles.some(item => item.id === file.id);
                        const isAllowed =
                          file.is_folder ||
                          !acceptedMimeTypes ||
                          acceptedMimeTypes.has(file.mime_type);

                        return (
                          <UnstyledButton
                            key={file.id}
                            onClick={isAllowed ? () => onFileClick(file) : undefined}
                            style={{
                              backgroundColor: isSelected
                                ? 'light-dark(#eef4ff, var(--mantine-color-blue-light))'
                                : '#fff',
                              border: `1px solid ${isSelected ? 'light-dark(#bfd3ff, var(--mantine-color-blue-light-hover))' : surfaceBorderColor}`,
                              borderRadius: 10,
                              cursor: isAllowed ? 'pointer' : 'not-allowed',
                              opacity: isAllowed ? 1 : 0.45,
                              padding: '10px 12px',
                              textAlign: 'left',
                              width: '100%'
                            }}
                          >
                            <Group justify="space-between" wrap="nowrap" gap="sm">
                              <Group wrap="nowrap" gap="sm" style={{ minWidth: 0 }}>
                                <Box
                                  style={{
                                    alignItems: 'center',
                                    backgroundColor:
                                      'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
                                    borderRadius: 10,
                                    color: file.is_folder ? '#d79b00' : mutedTextColor,
                                    display: 'flex',
                                    height: 36,
                                    justifyContent: 'center',
                                    width: 36
                                  }}
                                >
                                  {file.is_folder ? (
                                    <TbFolder size={18} />
                                  ) : (
                                    <TbCloudUpload size={18} />
                                  )}
                                </Box>

                                <div style={{ minWidth: 0 }}>
                                  <Text size="sm" fw={600} truncate>
                                    {file.name}
                                  </Text>
                                  <Text size="xs" c="dimmed" truncate>
                                    {getFileMeta(file, isCloudFolderNavigationEnabled)}
                                  </Text>
                                </div>
                              </Group>

                              {file.is_folder ? (
                                <TbChevronRight
                                  size={18}
                                  color="light-dark(#9ca3af, var(--mantine-color-dark-2))"
                                />
                              ) : !isAllowed ? (
                                <Badge variant="light" color="gray" size="sm">
                                  Not supported
                                </Badge>
                              ) : isSelected ? (
                                <Badge leftSection={<TbCheck size={12} />}>Selected</Badge>
                              ) : null}
                            </Group>
                          </UnstyledButton>
                        );
                      })
                    )}
                  </Stack>
                </ScrollArea>
              </Stack>

              <Group
                justify="space-between"
                px="md"
                py="sm"
                style={{ borderTop: `1px solid ${surfaceBorderColor}` }}
              >
                <Text size="sm" c="dimmed">
                  <Text span fw={700} c="var(--dx-c-dark-6)">
                    {selectedFilesCount}
                  </Text>{' '}
                  files selected
                </Text>
                <Button
                  onClick={onImport}
                  loading={isImporting}
                  disabled={selectedFilesCount === 0}
                >
                  Import selected
                </Button>
              </Group>
            </Stack>
          </Group>
        </Tabs.Panel>

        <Tabs.Panel value="integrations">
          <Stack gap="md" p="lg">
            <TextInput
              value={integrationQuery}
              onChange={event => setIntegrationQuery(event.target.value)}
              placeholder="Search integrations..."
              leftSection={
                <FiSearch size={14} color="light-dark(#9ca3af, var(--mantine-color-dark-2))" />
              }
              styles={{
                input: {
                  backgroundColor: 'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
                  borderColor: 'transparent'
                }
              }}
            />

            <div>
              <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb="sm">
                Available integrations
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
                {filteredProviders.map(provider => {
                  const providerMeta = getProviderMeta(provider.key) || provider;
                  const providerConnections = connections.filter(
                    connection =>
                      (connection?.provider_key ||
                        connection?.provider ||
                        connection?.provider_config_key) === provider.key
                  );
                  const isConnected = providerConnections.length > 0;
                  const isSelected = selectedProvider === provider.key;

                  return (
                    <Card
                      key={provider.key}
                      withBorder
                      radius="md"
                      p="md"
                      style={{
                        backgroundColor: isSelected
                          ? 'light-dark(#eef4ff, var(--mantine-color-blue-light))'
                          : '#fff',
                        borderColor: isSelected
                          ? 'light-dark(#bfd3ff, var(--mantine-color-blue-light-hover))'
                          : surfaceBorderColor
                      }}
                    >
                      <Stack gap="md">
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <Box
                            style={{
                              alignItems: 'center',
                              backgroundColor: 'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
                              borderRadius: 12,
                              display: 'flex',
                              height: 42,
                              justifyContent: 'center',
                              width: 42
                            }}
                          >
                            <ProviderLogo
                              alt={provider.label}
                              logoPath={provider.logoPath}
                              size={20}
                            />
                          </Box>

                          {isConnected ? (
                            <Badge variant="light" color="green">
                              Connected
                            </Badge>
                          ) : !providerMeta.canConnect ? (
                            <Badge variant="light" color="gray">
                              Locked
                            </Badge>
                          ) : null}
                        </Group>

                        <div>
                          <Text fw={600} size="sm" mb={4}>
                            {provider.label}
                          </Text>
                          <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
                            {providerMeta.description || `Browse files from ${provider.label}.`}
                          </Text>
                        </div>

                        <Group justify="space-between" wrap="nowrap">
                          <Button
                            variant={isSelected ? 'filled' : 'default'}
                            size="xs"
                            disabled={!isConnected && !providerMeta.canConnect}
                            onClick={() => {
                              onSelectProvider(provider.key);
                              setActiveTab('browse');
                            }}
                          >
                            {!isConnected && !providerMeta.canConnect ? 'Locked' : 'Browse'}
                          </Button>

                          {!isConnected && providerMeta.canConnect ? (
                            <Button
                              variant="subtle"
                              size="xs"
                              onClick={() => {
                                onSelectProvider(provider.key);
                                onConnectProvider(provider.key);
                              }}
                            >
                              Connect
                            </Button>
                          ) : null}
                        </Group>
                      </Stack>
                    </Card>
                  );
                })}

                <Card
                  withBorder
                  radius="md"
                  p="md"
                  style={{ borderColor: surfaceBorderColor, cursor: 'pointer' }}
                  onClick={onShowMoreIntegrations}
                >
                  <Stack gap="md" h="100%" justify="space-between">
                    <Box
                      style={{
                        alignItems: 'center',
                        backgroundColor: 'light-dark(#f5f6f8, var(--mantine-color-dark-6))',
                        borderRadius: 12,
                        display: 'flex',
                        height: 42,
                        justifyContent: 'center',
                        width: 42
                      }}
                    >
                      <TbChevronRight size={20} />
                    </Box>

                    <div>
                      <Text fw={600} size="sm" mb={4}>
                        Show more
                      </Text>
                      <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
                        Explore the full integrations catalog and see which sources are already
                        connected.
                      </Text>
                    </div>

                    <Button variant="default" size="xs" onClick={onShowMoreIntegrations}>
                      Open integrations
                    </Button>
                  </Stack>
                </Card>
              </SimpleGrid>
            </div>

            <Divider />

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                After connecting a source, return to the Browse files tab to select files.
              </Text>
              <Button variant="default" onClick={() => setActiveTab('browse')}>
                Back to browse
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
};

export default CloudImportModal;
